/* eslint-disable @typescript-eslint/no-floating-promises, @typescript-eslint/no-explicit-any */
import React, { Fragment, useMemo, useState } from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { Link } from 'react-router-dom';

import SimpleView from './PlacementEditor/SimpleView';
import AdvancedView from './PlacementEditor/AdvancedView';
import ExportPlacement from './PlacementEditor/ExportPlacement';
import IndexedList from '../support/shared/IndexedList';

import Dropdown from './uiold/Dropdown';
import LoadingIndicator from './uiold/LoadingIndicator';
import {
  Page,
  Button,
  Icon,
  Separator,
  SegmentedControl,
  Text,
  Tooltip,
  Modal,
} from './uiold';
import { themeColors } from './uiold/Constants';
import connectData, { ConnectedProps } from '../support/connectData';
import { canUser } from '../support/shared/permissionHelpers';

import { Account } from '../types/shared/Account';
import { Market } from '../types/shared/Market';
import { VendorList } from '../types/shared/Vendor';
import { Tag } from '../types/shared/Tag';

const PlacementViews = {
  simple: { title: t('Simple View'), value: 'simple' },
  advanced: { title: t('Advanced View'), value: 'advanced' },
};

function PlacementEditor(props: FullProps) {
  const {
    attendance,
    vendors,
    markets,
    placement,
    isDirty,
    isSaving,
    isLoading,
    positionMap,
    filteredMarkets,
    sortedVendors,
    savePlacement,
    fetchAll,
    tags,
    account,
    user,
  } = props;

  const [activeMarketId, setActiveMarketId] = useState<string | null>(null);
  const [activeView, setActiveView] = useState(PlacementViews.advanced.value);
  const [isModalLegend, setIsModalLegend] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);

  const sortedMarkets = useMemo(() => {
    if (filteredMarkets) {
      return filteredMarkets
        .toArray()
        .sort((a, b) => a.name.localeCompare(b.name));
    } else {
      return undefined;
    }
  }, [filteredMarkets]);

  const onMarketSelect = (market: Market) => {
    if (market) {
      setActiveMarketId(market.id);
    }
  };

  const onViewChange = (activeView: string) => {
    if (activeView) {
      setActiveView(activeView);
    }
  };

  const toggleExportDialog = () => {
    setShowExportDialog(!showExportDialog);
  };

  const hideExportDialog = () => {
    setShowExportDialog(false);
  };

  const openModalLegend = () => {
    setIsModalLegend(true);
  };
  const closeModalLegend = () => {
    setIsModalLegend(false);
  };

  const isSimpleView = activeView === PlacementViews.simple.value;

  const market = getActiveMarket(activeMarketId, sortedMarkets ?? []);
  let positionName: ObjectOf<string> = {};

  if (positionMap && market) {
    let value = positionMap[market.id];
    if (value) {
      positionName = value;
    }
  }

  if (
    isLoading ||
    !markets ||
    !vendors ||
    !sortedVendors ||
    !placement ||
    !sortedMarkets ||
    !positionMap
  ) {
    return (
      <Page>
        <Page.Header title={t('Placement')} showSeparator={false} />
        <Separator />
        <LoadingIndicator />
      </Page>
    );
  }
  if (!market) {
    return (
      <Page>
        <Page.Header title={t('Placement')} showSeparator={false} />
        <Separator style={{ marginBottom: 20 }} />
        <View style={{ padding: 20 }}>
          <Text>
            {t.jsx(
              "You don't have any markets. <link>Click here</link> to edit your markets.",
              { link: (content) => <Link to="/markets">{content}</Link> },
            )}
          </Text>
        </View>
      </Page>
    );
  }

  const saveButton = (
    <View style={styles.saveWrapper}>
      {isSaving ? (
        <LoadingIndicator />
      ) : (
        <Fragment>
          <Button title={t('Revert')} onPress={() => fetchAll()} />
          <View style={styles.saveButton}>
            <Button
              title={t('Save')}
              onPress={() => {
                savePlacement();
              }}
              isPrimary
            />
          </View>
        </Fragment>
      )}
    </View>
  );

  let canEdit = canUser(user, { vendorPlacement: 'WRITE' });

  return (
    <Page>
      <Page.Header title={t('Placement')} showSeparator={false} />
      <Separator />
      <View style={styles.toolbarDiv}>
        <View style={styles.toolbar}>
          <Dropdown
            options={sortedMarkets}
            value={activeMarketId}
            onChange={onMarketSelect}
          />
          {isDirty && saveButton}
          {!isSimpleView && (
            <TouchableOpacity
              onPress={openModalLegend}
              style={styles.questionMarkIcon}
            >
              <Icon name="help" size="S" color={themeColors.black} />
            </TouchableOpacity>
          )}
        </View>
        <View style={styles.rightToolbar}>
          <Tooltip title={t('Export')}>
            {!isSimpleView && (
              <TouchableOpacity
                onPress={toggleExportDialog}
                style={styles.touchableIcon}
              >
                <Icon size="S" name="export" />
              </TouchableOpacity>
            )}
          </Tooltip>
          <View style={styles.segmentedControls}>
            <SegmentedControl
              activeSegment={activeView}
              segments={[PlacementViews.simple, PlacementViews.advanced]}
              onChange={onViewChange}
            />
          </View>
        </View>
      </View>
      {isSimpleView ? (
        <SimpleView
          key={market.id}
          market={market}
          vendors={sortedVendors}
          placement={placement}
          positionMap={positionMap}
          tags={tags || []}
          canEdit={canEdit}
        />
      ) : (
        <AdvancedView
          tags={tags}
          key={market.id}
          attendance={attendance}
          vendors={sortedVendors}
          market={market}
          placementByVendor={placement}
          positionMap={positionName}
          canEdit={canEdit}
        />
      )}
      {isModalLegend && <ModalLegend closeModalLegend={closeModalLegend} />}
      <ExportDialog
        account={account}
        markets={markets}
        tags={tags || []}
        showExportDialog={showExportDialog}
        activeView={activeView}
        hideExportDialog={hideExportDialog}
      />
    </Page>
  );
}

function getActiveMarket(
  activeMarketId: string | null,
  markets: Array<Market>,
): Market | undefined {
  if (activeMarketId) {
    return markets.find((market) => market.id === activeMarketId);
  }
  return markets[0];
}

function ModalLegend(props: {
  closeModalLegend: (...args: Array<any>) => any;
}) {
  const { closeModalLegend } = props;
  return (
    <Modal
      isVisible
      title={t('Keyboard Shortcuts')}
      onClose={closeModalLegend}
      style={styles.placementModal}
    >
      <Modal.Body style={styles.modalBody}>
        <Text>
          {t(
            '1. Hold ⌘ (for Mac) / CTRL (for Windows) to select/deselect multiple cells.',
          )}
        </Text>
        <Separator direction="horizontal" style={styles.modalSeparator} />
        <Text>{t('2. Hold Shift to select an area of cells.')}</Text>
        <Separator direction="horizontal" style={styles.modalSeparator} />
        <Text>{t('3. Click Space to select/deselect one cell.')}</Text>
        <Separator direction="horizontal" style={styles.modalSeparator} />
        <Text>
          {t('4. Click on Top Date Cell to select the entire column.')}
        </Text>
        <Separator direction="horizontal" style={styles.modalSeparator} />
        <Text>
          {t('5. Click on Stall Number Cell to select the entire row.')}
        </Text>
        <Separator direction="horizontal" style={styles.modalSeparator} />
      </Modal.Body>
    </Modal>
  );
}

function ExportDialog(props: {
  account: Account | undefined;
  markets: IndexedList<Market>;
  tags: Array<Tag>;
  showExportDialog: boolean;
  activeView: string;
  hideExportDialog: (...args: Array<any>) => any;
}) {
  const {
    account,
    markets,
    tags,
    showExportDialog,
    activeView,
    hideExportDialog,
  } = props;
  if (
    account &&
    showExportDialog &&
    activeView === PlacementViews.advanced.value
  ) {
    return (
      <ExportPlacement
        tags={tags}
        account={account}
        markets={markets}
        onHide={hideExportDialog}
      />
    );
  }
  return null;
}

const styles = StyleSheet.create({
  // TODO: Move common style to global style
  placementModal: {
    padding: 0,
    width: 680,
    minHeight: 200,
    maxHeight: 600,
  },
  modalBody: {
    padding: 40,
    justifyContent: 'space-around',
    flex: 1,
  },
  toolbarDiv: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  rightToolbar: {
    flexDirection: 'row',
    paddingVertical: 20,
  },
  segmentedControls: {
    minWidth: 300,
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 20,
  },
  saveWrapper: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  saveButton: {
    marginLeft: 10,
  },
  questionMarkIcon: {
    paddingHorizontal: 20,
  },
  modalSeparator: {
    marginBottom: 20,
  },
  touchableIcon: {
    borderWidth: 1,
    borderColor: themeColors.borderGrey,
    backgroundColor: themeColors.white,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 3,
    height: 34,
    width: 34,
    marginRight: 7,
  },
});

const connector = connectData()(
  () => ({
    account: { dataStoreKey: 'account' },
    user: { dataStoreKey: 'user' },
    attendance: { dataStoreKey: 'attendance' },
    markets: { dataStoreKey: 'markets' },
    vendors: { dataStoreKey: 'vendors/all' },
    placement: { dataStoreKey: 'placement' },
    positionMap: { dataStoreKey: 'position' },
    tags: { dataStoreKey: 'tags' },
  }),
  () => ({
    savePlacement: {
      dataStoreKey: 'placement',
    },
  }),
  {
    computedProps: {
      filteredMarkets: {
        props: ['markets'],
        compute: (markets: IndexedList<Market> | null | undefined) => {
          if (!markets) {
            return null;
          }
          return markets.filter(
            (market) => market.dates != null && market.dates.length !== 0,
          );
        },
      },
      sortedVendors: {
        props: ['vendors'],
        compute: (vendors: VendorList | null | undefined) => {
          return vendors ? vendors.sortByKey('name') : null;
        },
      },
    },
  },
);
type FullProps = ConnectedProps<typeof connector>;
export default connector(PlacementEditor);
